import { useState, useMemo } from 'react'
import { Link, Clock, Radio, CheckCircle, ChevronUp, ChevronDown } from 'lucide-react'

const eventIcons = {
  upcoming: <Clock size={11} />,
  live: <Radio size={11} />,
  completed: <CheckCircle size={11} />,
}

// Sort priority for event_type
const eventOrder = { live: 0, upcoming: 1, completed: 2 }

export default function YTVideoList({ videos, loading, selected, onSelect, channels, checked, onToggle, onToggleAll }) {
  const [sortKey, setSortKey] = useState(null)
  const [sortDir, setSortDir] = useState('asc')

  const channelMap = {}
  for (const ch of channels) channelMap[ch.youtube_channel_id] = ch.name

  const allChecked = videos.length > 0 && videos.every(v => checked.has(v.youtube_id))

  function handleSort(key) {
    if (sortKey === key) {
      if (sortDir === 'asc') setSortDir('desc')
      else { setSortKey(null); setSortDir('asc') } // third click clears sort
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const sortedVideos = useMemo(() => {
    if (!sortKey) return videos
    const sorted = [...videos].sort((a, b) => {
      let aVal, bVal
      switch (sortKey) {
        case 'title':
          aVal = (a.title || '').toLowerCase()
          bVal = (b.title || '').toLowerCase()
          return aVal.localeCompare(bVal)
        case 'channel':
          aVal = (channelMap[a.youtube_channel_id] || '').toLowerCase()
          bVal = (channelMap[b.youtube_channel_id] || '').toLowerCase()
          return aVal.localeCompare(bVal)
        case 'status':
          aVal = eventOrder[a.event_type] ?? 99
          bVal = eventOrder[b.event_type] ?? 99
          return aVal - bVal
        case 'date':
          // dev24: fall back to published_at (YouTube upload date) so VOD
          // videos (no scheduled_start) sort chronologically with live events.
          aVal = a.scheduled_start ? new Date(a.scheduled_start).getTime()
               : a.published_at ? new Date(a.published_at).getTime() : 0
          bVal = b.scheduled_start ? new Date(b.scheduled_start).getTime()
               : b.published_at ? new Date(b.published_at).getTime() : 0
          return aVal - bVal
        case 'duration':
          aVal = a.duration_minutes || 0
          bVal = b.duration_minutes || 0
          return aVal - bVal
        case 'linked':
          aVal = a.tbtv_id ? 1 : 0
          bVal = b.tbtv_id ? 1 : 0
          return aVal - bVal
        case 'assigned':
          aVal = (a.viloud_channel_name || '').toLowerCase()
          bVal = (b.viloud_channel_name || '').toLowerCase()
          return aVal.localeCompare(bVal)
        default:
          return 0
      }
    })
    return sortDir === 'desc' ? sorted.reverse() : sorted
  }, [videos, sortKey, sortDir, channelMap])

  function SortHeader({ label, sortId, className }) {
    const active = sortKey === sortId
    return (
      <th className={`${className} th-sortable`} onClick={() => handleSort(sortId)}>
        <span className="th-sort-inner">
          {label}
          {active ? (
            sortDir === 'asc' ? <ChevronUp size={12} /> : <ChevronDown size={12} />
          ) : (
            <span className="th-sort-idle">⇅</span>
          )}
        </span>
      </th>
    )
  }

  function formatDate(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(dateStr)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  function formatTime(dateStr) {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  }

  function formatDuration(mins) {
    if (!mins) return '—'
    const h = Math.floor(mins / 60)
    const m = mins % 60
    return h > 0 ? `${h}h ${m}m` : `${m}m`
  }

  if (loading) {
    return (
      <div className="yt-table-wrap">
        <table className="yt-table">
          <thead><tr><th colSpan="9" style={{ textAlign: 'center', padding: 20, color: '#999' }}>Loading...</th></tr></thead>
        </table>
      </div>
    )
  }

  return (
    <div className="yt-table-wrap">
      <table className="yt-table">
        <thead>
          <tr>
            <th className="th-check">
              <input
                type="checkbox"
                checked={allChecked}
                onChange={onToggleAll}
              />
            </th>
            <th className="th-thumb">Thumb</th>
            <SortHeader label="Title" sortId="title" className="th-title" />
            <SortHeader label="Channel" sortId="channel" className="th-channel" />
            <SortHeader label="Status" sortId="status" className="th-status" />
            <SortHeader label="Scheduled" sortId="date" className="th-date" />
            <SortHeader label="Duration" sortId="duration" className="th-duration" />
            <SortHeader label="VOD" sortId="linked" className="th-linked" />
            <SortHeader label="Assigned" sortId="assigned" className="th-assigned" />
          </tr>
        </thead>
        <tbody>
          {sortedVideos.length === 0 ? (
            <tr><td colSpan="9" className="yt-table-empty">No videos found. Try syncing channels or adjusting filters.</td></tr>
          ) : (
            sortedVideos.map(v => (
              <tr
                key={v.youtube_id}
                className={`${selected?.youtube_id === v.youtube_id ? 'active' : ''} ${checked.has(v.youtube_id) ? 'checked' : ''}`}
                onClick={() => onSelect(v)}
              >
                <td className="td-check" onClick={e => { e.stopPropagation(); onToggle(v.youtube_id) }}>
                  <input
                    type="checkbox"
                    checked={checked.has(v.youtube_id)}
                    onChange={() => {}}
                  />
                </td>
                <td className="td-thumb">
                  {(v.thumbnail_override_url || v.thumbnail_url) ? (
                    <img src={v.thumbnail_override_url || v.thumbnail_url} alt="" className={v.thumbnail_override_url ? 'yt-thumb-overridden' : ''} />
                  ) : (
                    <div className="yt-thumb-placeholder" />
                  )}
                </td>
                <td className="td-title" title={v.title}>
                  {v.title || 'Untitled'}
                </td>
                <td className="td-channel">
                  {channelMap[v.youtube_channel_id] || '—'}
                </td>
                <td className="td-status">
                  <span className={`yt-event-badge ${v.event_type}`}>
                    {eventIcons[v.event_type]} {v.event_type}
                  </span>
                </td>
                <td className="td-date">
                  {v.scheduled_start ? (
                    <>
                      <span className="date-primary">{formatDate(v.scheduled_start)}</span>
                      <span className="date-time">{formatTime(v.scheduled_start)}</span>
                    </>
                  ) : v.published_at ? (
                    // dev24: VOD videos (playlist-sourced or completed channel VOD)
                    // don't have scheduled_start. Fall back to the YouTube upload
                    // date (published_at) with a "uploaded" label so editors can
                    // distinguish it from a real scheduled date.
                    <span title="YouTube upload date (no scheduled start)">
                      <span className="date-primary">{formatDate(v.published_at)}</span>
                      <span className="date-time" style={{ fontStyle: 'italic', opacity: 0.7 }}>uploaded</span>
                    </span>
                  ) : '—'}
                </td>
                <td className="td-duration">
                  {formatDuration(v.duration_minutes)}
                </td>
                <td className="td-linked">
                  {v.tbtv_id ? (
                    <span className="yt-linked-badge"><Link size={10} /> Linked</span>
                  ) : (
                    <span className="yt-unlinked">—</span>
                  )}
                </td>
                <td className="td-assigned">
                  {v.viloud_channel_name || '—'}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  )
}
